import React, { Fragment, useCallback, useState, useEffect } from "react";
import { debounce } from "utils";
import { TableRowLoader, TableActionLoader } from "components/loaders";
import * as Components from "components";
import { getTickets } from "services/intercom.service";
import { SyncIcon } from "assets/icons";
import { ticketsTableMenuOptions } from "content/Dropdown";
import { Space } from "antd";
import { useNavigate } from "react-router";
import { TicketTableColumns } from "content/TableColumns";
import {
  TicketDefaultFilters,
  TicketDefaultMulti,
} from "content/DefaultFilter";
import { toast } from "react-toastify";

const AllTickets = () => {
  const navigate = useNavigate();
  const [checkedTableMenuColumn, setCheckedTableMenuColumn] =
    useState(TicketTableColumns);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filter, setFilter] = useState(TicketDefaultFilters);
  const [search, setSearch] = useState("");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [paginationData, setPaginationData] = useState({
    total_count: 0,
    current_page: 1,
    total_pages: 0,
    per_page: 10,
    has_more: false,
    next_cursor: null,
  });

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    fetchTickets(true); // true = reset/fresh load
  }, [
    filter.status,
    filter.category,
    filter.dateFrom,
    filter.dateTo,
    filter.search,
  ]);

  // Memoized debounced search function
  const debouncedSearch = useCallback(
    debounce((value) => {
      handleFilter("search", value);
    }, 500),
    []
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  // Fetch tickets from API
  const fetchTickets = async (reset = false) => {
    if (reset) {
      setLoading(true);
      setTickets([]);
    } else {
      setLoadingMore(true);
    }

    setError(null);

    try {
      const queryParams = {
        ...filter,
        starting_after: reset ? "" : paginationData.next_cursor || "",
      };

      const response = await getTickets(queryParams);

      // Handle different response structures
      const responseData = response.data.response || response.data;
      const ticketsData = responseData.tickets || responseData.data || [];
      const pages = responseData.pages || {};
      const totalCount = responseData.total_count || 0;

      if (reset) {
        setTickets(ticketsData);
      } else {
        setTickets((prev) => [...prev, ...ticketsData]);
      }

      // Update pagination info
      setPaginationData({
        total_count: totalCount,
        current_page: pages.page || 1,
        total_pages: pages.total_pages || 0,
        per_page: parseInt(pages.per_page) || 10,
        has_more: !!pages.next,
        next_cursor: pages.next?.starting_after || null,
      });
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setError("Failed to fetch tickets. Please try again.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load more tickets
  const handleLoadMore = () => {
    if (paginationData.has_more && !loadingMore) {
      fetchTickets(false); // false = append to existing
    }
  };

  // Handle filter changes
  const handleFilter = (name, value) => {
    if (name === "filter") {
      setFilter((prev) => ({
        ...prev,
        ...value,
        starting_after: "", // Reset pagination when filters change
      }));
    } else {
      setFilter((prev) => ({
        ...prev,
        [name]: value,
        starting_after: "", // Reset pagination when filters change
      }));
    }
    setSelectedRowKeys([]);
  };

  // Handle multi-category filter changes
  const handleFilterChange = (selectedFilters) => {
    const combinedFilters = Object.values(selectedFilters).flat();

    const formattedFilters = combinedFilters.reduce(
      (acc, item) => {
        if (["submitted", "in_progress", "resolved", "closed"].includes(item)) {
          acc.status = item;
        } else if (["Customer", "Back-office", "Tracker"].includes(item)) {
          acc.category = item;
        } else if (item.fromDate || item.toDate) {
          acc.dateFrom = item.fromDate;
          acc.dateTo = item.toDate;
        }
        return acc;
      },
      { status: "", category: "", dateFrom: "", dateTo: "" }
    );

    handleFilter("filter", formattedFilters);
  };

  // Handle row selection
  const onRowSelectionChange = (selectedKeys) => {
    setSelectedRowKeys(selectedKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onRowSelectionChange,
  };

  // Get status badge component
  const getStatusBadge = (status) => {
    const statusConfig = {
      submitted: {
        bg: "bg-blue-50 dark:bg-blue-900/20",
        text: "text-blue-600 dark:text-blue-400",
        label: "Submitted",
      },
      in_progress: {
        bg: "bg-alpha/10",
        text: "text-alpha",
        label: "In Progress",
      },
      resolved: {
        bg: "bg-green-50 dark:bg-green-900/20",
        text: "text-green-600 dark:text-green-400",
        label: "Resolved",
      },
      closed: {
        bg: "bg-gray-100 dark:bg-gray-800",
        text: "text-gray-600 dark:text-gray-400",
        label: "Closed",
      },
    };

    const config = statusConfig[status.category] || statusConfig.closed;

    return (
      <div
        className={`min-w-[100px] px-3 py-1 rounded-full text-center ${config.bg}`}
      >
        <div className={config.text}>{config.label}</div>
      </div>
    );
  };

  // Get assigned admin information
  const getAssignedTo = (ticket) => {
    if (ticket.admin_assignee_id && ticket.admin_assignee_id !== "0") {
      const assignmentParts =
        ticket.ticket_parts?.ticket_parts?.filter(
          (part) =>
            part.part_type === "assignment" &&
            part.assigned_to?.type === "admin"
        ) || [];

      if (assignmentParts.length > 0) {
        const latestAssignment = assignmentParts[assignmentParts.length - 1];
        return (
          latestAssignment.author?.name ||
          `Admin ID: ${ticket.admin_assignee_id} ${
            latestAssignment.author?.type &&
            `(${latestAssignment.author?.type})`
          }`
        );
      }

      const otherAssignee =
        ticket.ticket_parts?.ticket_parts?.filter(
          (part) => part.assigned_to?.id === ticket.admin_assignee_id
        ) || [];

      return `Admin ID: ${ticket.admin_assignee_id} (${
        otherAssignee[otherAssignee.length - 1]?.assigned_to?.type
      })`;
    }
    return "Unassigned";
  };

  // Get customer information
  const getCustomerInfo = (ticket) => {
    const userPart = ticket.ticket_parts?.ticket_parts?.find(
      (part) => part.author?.type === "user"
    );

    if (userPart) {
      return {
        name: userPart.author.name || "--",
        email: userPart.author.email || "--",
      };
    }

    return {
      name: "--",
      email: "--",
    };
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return "--";
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  // Handle menu actions
  const handleMenuClick = (key, record) => {
    switch (key) {
      case "view":
        navigate(`/tickets/${record.id}`);
        break;
      case "edit":
        // Handle edit action
        break;
      case "delete":
        // Handle delete action
        break;
      default:
        break;
    }
  };

  // Table columns definition
  const columns = [
    {
      title: "TICKET ID",
      dataIndex: "ticket_id",
      loader: <TableRowLoader />,
      render: (_, record) => (
        <span
          className="font-mono text-sm font-semibold text-primary cursor-pointer"
          onClick={() => navigate(`/tickets/${record.id}`)}
        >
          #{record.ticket_id}
        </span>
      ),
    },
    {
      title: "TITLE",
      dataIndex: "title",
      loader: <TableRowLoader />,
      render: (_, record) => (
        <div
          className="max-w-xs cursor-pointer"
          onClick={() => navigate(`/tickets/${record.id}`)}
        >
          <div className="font-medium truncate">
            {record.ticket_attributes?._default_title_ || "No Title"}
          </div>
          <div className="text-sm text-gray-500 mt-1">
            {record.ticket_type?.name || "--"} • {record.category}
          </div>
        </div>
      ),
    },
    {
      title: "CUSTOMER",
      dataIndex: "customer",
      loader: <TableRowLoader />,
      render: (_, record) => {
        const customer = getCustomerInfo(record);
        return (
          <div className="flex flex-col gap-1">
            <div className="font-medium">{customer.name}</div>
            <div className="text-sm text-gray-500">{customer.email}</div>
          </div>
        );
      },
    },
    {
      title: "TYPE",
      dataIndex: "type",
      loader: <TableRowLoader />,
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <span className="text-lg">{record.ticket_type?.icon || "📋"}</span>
          <span>{record.ticket_type?.name || "--"}</span>
        </div>
      ),
    },
    {
      title: "STATUS",
      dataIndex: "status",
      loader: <TableRowLoader />,
      render: (_, record) => getStatusBadge(record.ticket_state),
    },
    {
      title: "ASSIGNED TO",
      dataIndex: "assigned",
      loader: <TableRowLoader />,
      render: (_, record) => (
        <div className="text-sm">{getAssignedTo(record)}</div>
      ),
    },
    {
      title: "",
      dataIndex: "action",
      loader: <TableActionLoader />,
      render: (_, record) => (
        <Space size="middle" className="flex justify-center">
          <Components.DropDownMenu
            menuItems={ticketsTableMenuOptions}
            onMenuClick={(actionKey) => handleMenuClick(actionKey, record)}
          />
        </Space>
      ),
    },
  ];

  // Filter visible columns
  const visibleColumns = columns.filter(
    (column) =>
      column.dataIndex === "action" ||
      checkedTableMenuColumn.some(
        (col) => col.dataIndex === column.dataIndex && col.visible
      )
  );

  return (
    <Fragment>
      <div className="flex flex-col border dark:border-gray-800 my-5 2xl:my-10 mx-5 2xl:mx-14 rounded-lg shadow-lg dark:bg-bgprimary">
        {/* Header with filters and controls */}
        <div className="w-full flex items-center px-5 py-3">
          <div className="w-full flex gap-5">
            <div className="flex gap-5 items-center w-full xl:w-2/3">
              <div className="w-full xl:w-[400px]">
                <Components.SearchField
                  mobilePlaceholder="Search"
                  placeholder="Search by title or description"
                  value={search}
                  onChange={handleSearchChange}
                />
              </div>
              <Components.MultiCategoryFilter
                categories={TicketDefaultMulti}
                onFilterChange={handleFilterChange}
              />
            </div>
            <div className="flex items-center justify-end xl:w-1/3">
              <button
                onClick={() => fetchTickets(true)}
                className="btn btn-outline btn-sm gap-2 mr-2"
                disabled={loading}
              >
                <SyncIcon /> Refresh
              </button>
              <Components.TableCustomizeColumnMenu
                setCheckedTableMenuColumn={setCheckedTableMenuColumn}
                checkedTableMenuColumn={checkedTableMenuColumn}
              />
            </div>
          </div>
        </div>

        <div>
          <Components.CustomTable
            columns={visibleColumns}
            dataSource={tickets}
            pagination={false}
            // rowSelection={rowSelection}
            isLoading={loading}
          />
        </div>

        <div className="flex justify-between items-center my-5 px-5">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {paginationData.total_count > 0 && (
              <span>
                Showing {tickets.length} of {paginationData.total_count} tickets
                {paginationData.total_pages > 0 && (
                  <span>
                    {" "}
                    • Page {paginationData.current_page} of{" "}
                    {paginationData.total_pages}
                  </span>
                )}
              </span>
            )}
          </div>

          <div className="flex items-center gap-4">
            {paginationData.has_more && (
              <button
                className={`btn bg-secondary btn-sm border-none text-white hover:bg-secondary ${
                  loadingMore ? "loading" : ""
                }`}
                onClick={handleLoadMore}
                disabled={loadingMore || loading}
              >
                {loadingMore ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </button>
            )}

            {!paginationData.has_more && tickets.length > 0 && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                All tickets loaded
              </span>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AllTickets;
